import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  useV2CreateProjectLocationMutation,
  useV2UpdateProjectLocationMutation,
} from "@generated";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import AddressForm from "@components/Forms/AddressForm";
import ClientRecordPicker, {
  PickedRecord,
} from "@components/ClientRecordPicker/ClientRecordPicker";
import {
  siteLinkPayload,
  toCountryId,
} from "@components/ClientRecordPicker/siteLink";
import ProjectVariables from "../../ProjectVariables/ProjectVariables";
import { Location, ProjectVariable } from "../types/Location";
import useStyles from "../styles/useStyles";
import { formatDate } from "@utils/helperFunctions";

interface Props {
  formId?: string;
  projectId: string;
  clientId?: string | null;
  location?: Location;
  locations: Location[];
  projectVariableDefinitions: ProjectVariable[];
}

const LocationForm = ({
  formId,
  projectId,
  clientId,
  location,
  locations,
  projectVariableDefinitions,
}: Props): JSX.Element => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const navigate = useNavigate();
  const styles = useStyles();

  const isEdit = !!location;
  const today = new Date();

  // Address fields
  const [name, setName] = useState(location?.name || "");
  const [street, setStreet] = useState(location?.street || "");
  const [street2, setStreet2] = useState(location?.street2 || "");
  const [city, setCity] = useState(location?.city || "");
  const [state, setState] = useState(location?.state || "");
  const [postalCode, setPostalCode] = useState(location?.postalCode || "");
  const [country, setCountry] = useState(location?.country || "");
  const [countryId, setCountryId] = useState<string | null>(null);
  const [remote, setRemote] = useState(location?.remote ?? false);

  // A client location or ConnectWise site picked on create; the API links or
  // adopts it. Prefilled fields stay editable and edited values win.
  const [picked, setPicked] = useState<PickedRecord | null>(null);

  const applyPick = (rec: PickedRecord) => {
    setPicked(rec);
    setName(rec.fields.name ?? "");
    setStreet(rec.fields.street ?? "");
    setStreet2(rec.fields.street2 ?? "");
    setCity(rec.fields.city ?? "");
    setState(rec.fields.state ?? "");
    setPostalCode(rec.fields.postalCode ?? "");
    // CountryPicker wants an id ("us"); ConnectWise (and records adopted from
    // it) hold the name ("United States"). Keep the raw value if unknown.
    const pickedCountryId = toCountryId(rec.fields.country);
    setCountry(pickedCountryId ?? rec.fields.country ?? "");
    setCountryId(pickedCountryId ?? null);
  };

  // Project variables
  const [requiredProjectVariables, setRequiredProjectVariables] = useState<
    ProjectVariable[]
  >([]);
  const [unrequiredProjectVariables, setUnrequiredProjectVariables] = useState<
    ProjectVariable[]
  >([]);

  // Alerts
  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const [createLocation] = useV2CreateProjectLocationMutation();
  const [updateLocation] = useV2UpdateProjectLocationMutation();

  useEffect(() => {
    if (isEdit && location) {
      setName(location.name);
      setStreet(location.street);
      setStreet2(location.street2);
      setCity(location.city);
      setState(location.state);
      setPostalCode(location.postalCode);
      setCountry(location.country);
      setRemote(location.remote);

      const pvs = location.projectVariables || [];
      setRequiredProjectVariables(pvs.filter((v) => v.required === true));
      setUnrequiredProjectVariables(pvs.filter((v) => v.required === false));
    } else if (!isEdit && projectVariableDefinitions.length > 0) {
      const reqVars = projectVariableDefinitions
        .filter((v) => v.required)
        .map((v) => ({
          ...v,
          value: v.variable_type === "date" ? formatDate(today) : "",
        }));
      const unreqVars = projectVariableDefinitions
        .filter((v) => !v.required)
        .map((v) => ({ ...v, value: "" }));
      setRequiredProjectVariables(reqVars);
      setUnrequiredProjectVariables(unreqVars);
    }
  }, [location, projectVariableDefinitions]);

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!isEdit) {
      const duplicate = locations.some(
        (loc) => loc.name.toLowerCase() === name.toLowerCase()
      );
      if (duplicate) {
        errors.push(
          "Location name already exists. Please enter a different one."
        );
      }
    }

    if (!name.trim()) {
      errors.push("Project locations name can't be blank");
    }
    if (!remote) {
      if (!street.trim()) {
        errors.push("Project locations street can't be blank");
      }
      if (!city.trim()) {
        errors.push("Project locations city can't be blank");
      }
      if (!postalCode.trim()) {
        errors.push("Project locations postal code can't be blank");
      }
    }
    return errors;
  };

  const validateVariables = (allVars: ProjectVariable[]): string | null => {
    for (const variable of allVars) {
      const varType = variable.variable_type;
      if (
        varType === "text" &&
        (!variable.select_options || variable.select_options.length === 0)
      ) {
        const val = variable.value || "";
        if (variable.minimum != null && val.length < variable.minimum) {
          return `Required text is too short (minimum is ${variable.minimum} characters)`;
        }
        if (variable.maximum != null && val.length > variable.maximum) {
          return `Required text is too long (maximum is ${variable.maximum} characters)`;
        }
      }
    }
    return null;
  };

  const saveLocation = async () => {
    const formErrors = validateForm();
    if (formErrors.length > 0) {
      setErrorMessages(formErrors.join(", "));
      setShowFailAlert(true);
      return;
    }

    const allVars = requiredProjectVariables.concat(unrequiredProjectVariables);
    const varError = validateVariables(allVars);
    if (varError) {
      setErrorMessages(varError);
      setShowFailAlert(true);
      return;
    }

    const projectVariablesPayload = allVars.map((v) => ({
      name: v.name,
      value: v.value,
    }));

    const link = siteLinkPayload(isEdit ? null : picked);

    const payload = {
      data: {
        type: "project-locations" as const,
        ...(isEdit ? { id: location!.id } : {}),
        attributes: {
          name,
          street,
          street2,
          city,
          state,
          "postal-code": postalCode,
          country: countryId || country,
          remote,
          "project-variables": projectVariablesPayload,
          ...link.attributes,
        },
        relationships: {
          project: { data: { type: "projects", id: projectId } },
          ...link.relationships,
        },
      },
    };

    try {
      if (isEdit) {
        await updateLocation({
          id: location!.id,
          body: payload as any,
        }).unwrap();
        navigate(`/projects/${projectId}/project_locations`, {
          state: { edit: true },
        });
      } else {
        await createLocation({
          body: payload as any,
        }).unwrap();
        navigate(`/projects/${projectId}/project_locations`, {
          state: { new: true },
        });
      }
    } catch (err: any) {
      const message =
        err?.data?.errors?.[0]?.detail ||
        "Something went wrong. Your changes could not be saved.";
      setErrorMessages(message);
      setShowFailAlert(true);
    }
  };

  return (
    <>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          saveLocation();
        }}
      >
        {!isEdit && (
          <ClientRecordPicker
            kind="site"
            projectId={projectId}
            clientId={clientId}
            onPick={applyPick}
          />
        )}
        <AddressForm
          label="Location"
          placeholder="Customer Site"
          required={false}
          siteName={name}
          onSiteChange={(e: any) => {
            setName(e.target.value);
            if (!e.target.value) setPicked(null);
          }}
          remoteLocation={remote}
          onRemoteChanged={(e: any) => setRemote(e.target.checked)}
          addressText={street}
          onAddressChange={(e: any) => setStreet(e.target.value)}
          cityText={city}
          onCityChange={(e: any) => setCity(e.target.value)}
          stateText={state}
          onStateChange={(e: any) => setState(e.target.value)}
          zipText={postalCode}
          onZipChange={(e: any) => setPostalCode(e.target.value)}
          street2Text={street2}
          onStreet2Change={(e: any) => setStreet2(e.target.value)}
          country={country}
          onCountryChange={(e: any) => {
            setCountry(e);
            setCountryId(e.id);
          }}
        />
        <Row>
          <ProjectVariables
            arr={requiredProjectVariables}
            account_slug={accountSlug}
            project_id={projectId}
            setState={setRequiredProjectVariables}
            setVariableChanged={null}
          />
          <ProjectVariables
            arr={unrequiredProjectVariables}
            account_slug={accountSlug}
            project_id={projectId}
            setState={setUnrequiredProjectVariables}
            setVariableChanged={null}
          />
        </Row>
      </Form>
    </>
  );
};

export default LocationForm;
